import { useState } from 'react';
import { motion } from 'framer-motion';
import { useMutation } from '@tanstack/react-query';
import { Mail, Phone, MapPin, MessageCircle, Send, Clock, CheckCircle, ArrowRight } from 'lucide-react';
import SectionHeader from '@/components/SectionHeader';
import { submitContact } from '@/lib/api';

interface ContactForm {
  name: string;
  email: string;
  subject: string;
  orderNumber: string;
  message: string;
}

const supportChannels = [
  {
    id: 'whatsapp',
    label: 'WhatsApp',
    value: 'Message us',
    href: 'https://wa.me/971565628081',
    description: 'Fastest reply. Advisors typically respond in ~3 minutes during UAE business hours.',
    icon: MessageCircle,
  },
  {
    id: 'email',
    label: 'Email',
    value: 'birouti.husam@gmail.com',
    href: 'mailto:birouti.husam@gmail.com',
    description: 'General orders, shipping and account questions. Replies within 6 business hours.',
    icon: Mail,
  },
  {
    id: 'research',
    label: 'Research support',
    value: 'birouti.husam@gmail.com',
    href: 'mailto:birouti.husam@gmail.com',
    description: 'Lab-side and compound questions are looped to our AURA lab team directly.',
    icon: Mail,
  },
  {
    id: 'phone',
    label: 'Phone',
    value: '+971 56 562 8081',
    href: 'tel:+971565628081',
    description: 'Available weekdays 09:00–17:30 GMT.',
    icon: Phone,
  },
];

const containerVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function ContactPage() {
  const [form, setForm] = useState<ContactForm>({
    name: '',
    email: '',
    subject: '',
    orderNumber: '',
    message: '',
  });

  const mutation = useMutation({
    mutationFn: submitContact,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    mutation.mutate({
      name: form.name,
      email: form.email,
      message: `[Subject: ${form.subject || 'General enquiry'}] [Order: ${form.orderNumber || 'n/a'}] ${form.message}`,
    });
  };

  return (
    <div className="min-h-full bg-rp-paper">
      {/* Hero header */}
      <section className="relative overflow-hidden border-b border-rp-line bg-rp-bone">
        <div className="rp-container py-16 md:py-24">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            className="mx-auto max-w-3xl text-center"
          >
            <span className="rp-mono-mini text-rp-moss">Support</span>
            <h1 className="mt-4 font-sans text-h1 font-semibold tracking-tight text-rp-ink">
              Contact <em className="not-italic text-rp-forest">AURA</em>
            </h1>
            <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-rp-moss">
              Questions about an order, a product, or a protocol? Send us a message or start a WhatsApp chat — our specialist team replies within 6 business hours.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main contact content */}
      <section className="rp-container py-12 md:py-20">
        <div className="grid gap-10 lg:grid-cols-[1.2fr_0.8fr] lg:gap-16">
          {/* Contact form */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={containerVariants}
          >
            <SectionHeader
              eyebrow="Send a message"
              title="We read every message"
              description="Fill in the form below and we will get back to you as soon as possible. If your question is research-related, it will be forwarded to our AURA lab team."
              align="left"
            />

            {mutation.isSuccess ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="rounded-2xl border border-rp-line bg-rp-bone p-8 text-center md:p-12"
              >
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-rp-leaf-soft text-rp-forest">
                  <CheckCircle size={28} />
                </div>
                <h3 className="mt-6 text-xl font-semibold text-rp-ink">Message sent</h3>
                <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-rp-moss">
                  Thanks for reaching out, {form.name || 'there'}. A member of the AURA team will reply within 6 business hours.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    mutation.reset();
                    setForm({ name: '', email: '', subject: '', orderNumber: '', message: '' });
                  }}
                  className="rp-btn-primary mt-8"
                >
                  Send another message
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="rounded-2xl border border-rp-line bg-rp-bone p-6 md:p-8">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="name" className="font-mono text-[10px] font-bold uppercase tracking-[0.08em] text-rp-moss">
                      Name
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={form.name}
                      onChange={handleChange}
                      className="rounded-xl border border-rp-line bg-rp-paper px-4 py-3 text-sm text-rp-ink outline-none transition-colors placeholder:text-rp-moss/60 focus:border-rp-forest"
                      placeholder="Your name"
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label htmlFor="email" className="font-mono text-[10px] font-bold uppercase tracking-[0.08em] text-rp-moss">
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      className="rounded-xl border border-rp-line bg-rp-paper px-4 py-3 text-sm text-rp-ink outline-none transition-colors placeholder:text-rp-moss/60 focus:border-rp-forest"
                      placeholder="you@email.com"
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label htmlFor="subject" className="font-mono text-[10px] font-bold uppercase tracking-[0.08em] text-rp-moss">
                      Topic
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      className="rounded-xl border border-rp-line bg-rp-paper px-4 py-3 text-sm text-rp-ink outline-none transition-colors focus:border-rp-forest"
                    >
                      <option value="">Select a topic</option>
                      <option value="Order status">Order status</option>
                      <option value="Shipping">Shipping</option>
                      <option value="Returns & refunds">Returns & refunds</option>
                      <option value="Product question">Product question</option>
                      <option value="Protocol advice">Protocol advice</option>
                      <option value="Research enquiry">Research enquiry</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="flex flex-col gap-2">
                    <label htmlFor="orderNumber" className="font-mono text-[10px] font-bold uppercase tracking-[0.08em] text-rp-moss">
                      Order number <span className="font-normal normal-case text-rp-moss/60">(optional)</span>
                    </label>
                    <input
                      id="orderNumber"
                      name="orderNumber"
                      type="text"
                      value={form.orderNumber}
                      onChange={handleChange}
                      className="rounded-xl border border-rp-line bg-rp-paper px-4 py-3 text-sm text-rp-ink outline-none transition-colors placeholder:text-rp-moss/60 focus:border-rp-forest"
                      placeholder="RP-000000"
                    />
                  </div>
                </div>

                <div className="mt-5 flex flex-col gap-2">
                  <label htmlFor="message" className="font-mono text-[10px] font-bold uppercase tracking-[0.08em] text-rp-moss">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    value={form.message}
                    onChange={handleChange}
                    className="resize-none rounded-xl border border-rp-line bg-rp-paper px-4 py-3 text-sm text-rp-ink outline-none transition-colors placeholder:text-rp-moss/60 focus:border-rp-forest"
                    placeholder="How can we help?"
                  />
                </div>

                {mutation.isError && (
                  <p className="mt-4 text-sm text-rp-warn">
                    Something went wrong sending your message. Please try again or reach out on WhatsApp.
                  </p>
                )}

                <div className="mt-6 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-2 text-xs text-rp-moss">
                    <Clock size={14} />
                    <span>Typical response time: ~6 business hours</span>
                  </div>
                  <button
                    type="submit"
                    disabled={mutation.isPending}
                    className="rp-btn-primary disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {mutation.isPending ? (
                      <span className="flex items-center gap-2">
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-rp-paper border-t-transparent" />
                        Sending…
                      </span>
                    ) : (
                      <>
                        Send message
                        <Send size={14} />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </motion.div>

          {/* Contact channels */}
          <motion.aside
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={containerVariants}
            className="flex flex-col gap-6"
          >
            <div className="rounded-2xl border border-rp-line bg-rp-forest p-6 text-rp-paper md:p-8">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-rp-paper text-rp-forest">
                  <MessageCircle size={20} />
                </span>
                <div>
                  <h3 className="font-sans text-lg font-semibold">Prefer WhatsApp?</h3>
                  <p className="text-sm text-rp-leaf-soft">Fastest way to reach us.</p>
                </div>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-rp-leaf-soft">
                Chat with a specialist advisor for order updates, product guidance, or protocol questions.
              </p>
              <a
                href="https://wa.me/971565628081"
                target="_blank"
                rel="noopener noreferrer"
                className="rp-btn mt-6 w-full bg-rp-paper px-6 py-3 text-rp-forest hover:bg-rp-bone"
              >
                <MessageCircle size={14} />
                Start WhatsApp chat
                <ArrowRight size={14} />
              </a>
            </div>

            <div className="grid gap-4">
              {supportChannels.map((channel, index) => (
                <motion.a
                  key={channel.id}
                  href={channel.href}
                  target={channel.href.startsWith('http') ? '_blank' : undefined}
                  rel={channel.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08, duration: 0.4 }}
                  whileHover={{ y: -2 }}
                  className="group flex items-start gap-4 rounded-2xl border border-rp-line bg-rp-bone p-5 transition-shadow hover:shadow-card-hover"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-rp-line bg-rp-paper text-rp-forest transition-colors group-hover:border-rp-forest">
                    <channel.icon size={18} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-[10px] font-bold uppercase tracking-[0.08em] text-rp-moss">
                      {channel.label}
                    </p>
                    <p className="mt-0.5 truncate text-sm font-semibold text-rp-ink">{channel.value}</p>
                    <p className="mt-1 text-xs leading-relaxed text-rp-moss">{channel.description}</p>
                  </div>
                </motion.a>
              ))}
            </div>

            <div className="rounded-2xl border border-rp-line bg-rp-bone p-6">
              <div className="flex items-start gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-rp-line bg-rp-paper text-rp-forest">
                  <MapPin size={18} />
                </span>
                <div>
                  <p className="font-mono text-[10px] font-bold uppercase tracking-[0.08em] text-rp-moss">Dispatch address</p>
                  <address className="mt-1 not-italic text-sm leading-relaxed text-rp-ink">
                    AURA
                    <br />
                    Dubai
                    <br />
                    United Arab Emirates
                  </address>
                </div>
              </div>
            </div>
          </motion.aside>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-t border-rp-line bg-rp-bone">
        <div className="rp-container py-10 md:py-14">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4"
          >
            {[
              { label: 'Free worldwide shipping', value: 'On every order' },
              { label: 'HPLC-verified purity', value: 'Independent testing' },
              { label: 'Cold-chain dispatch', value: 'From UAE stock' },
              { label: '6-hour response', value: 'During business hours' },
            ].map((item) => (
              <div key={item.label} className="text-center sm:text-left">
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.08em] text-rp-forest">{item.label}</p>
                <p className="mt-1 text-sm text-rp-moss">{item.value}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
